import { useCallback, useEffect, useState } from "react";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import {
  OnestNormalDefault,
  OnestSemiBoldDefault,
} from "../../../components/styled/TextComponents";
import {
  color_black,
  color_red_default,
  color_white,
} from "../../../constants/colors";
import { useWindowDimensions } from "../../../utils";
import { useI18n } from "../../../i18n/I18nProvider";
import ScheduleDay from "./ScheduleDay";
import {
  getNormalizedWeekDaySunLast,
  getScheduleCalendarMinHeight,
  isTodayInTimeZone,
  MOBILE_CALENDAR_WIDTH,
} from "./scheduleConstants";

const ScheduleCalendar = ({
  schedule,
  weekDayRange,
  onOpenModalNote,
  onOpenModalReschedule,
  onOpenPersonalEventEdit,
  onDeletePersonalEvent,
}) => {
  const user = useSelector((state) => state.user);
  const { locale } = useI18n();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const calendarMinHeight = getScheduleCalendarMinHeight(
    screenWidth,
    screenHeight,
  );
  const timeZone =
    user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [currentDate, setCurrentDate] = useState(moment().day());
  const [openCardId, setOpenCardId] = useState(null);

  const handleToggleCard = useCallback((cardId) => {
    setOpenCardId((prev) => (prev === cardId ? null : cardId));
  }, []);

  const weekFullRange = () => {
    const weekDays = [];
    const weekStart = moment(weekDayRange[0]);

    while (
      getNormalizedWeekDaySunLast(weekStart.day()) <=
      getNormalizedWeekDaySunLast(weekDayRange[1].day())
    ) {
      weekDays.push(moment(weekStart));
      if (
        getNormalizedWeekDaySunLast(weekStart.day()) ===
        getNormalizedWeekDaySunLast(weekDayRange[1].day())
      ) {
        break;
      }
      weekStart.add(1, "days");
    }
    return weekDays;
  };

  const splitScheduleByWeekDays = (items) => {
    return weekFullRange().map((weekday) => ({
      weekday,
      lessons: items.filter((el) => moment(el.dateDate).day() === weekday.day()),
    }));
  };

  const [scheduleToDays, setScheduleToDays] = useState(
    splitScheduleByWeekDays(schedule),
  );

  useEffect(() => {
    setScheduleToDays(splitScheduleByWeekDays(schedule));
  }, [schedule, weekDayRange, user.time_zone]);

  const dayProps = (el, isToday) => ({
    weekday: el.weekday,
    day: el.weekday,
    lessons: el.lessons,
    isToday,
    onOpenModalNote,
    onOpenModalReschedule,
    onOpenPersonalEventEdit,
    onDeletePersonalEvent,
    openCardId,
    onToggleCard: handleToggleCard,
  });

  return (
    <>
      {screenWidth < MOBILE_CALENDAR_WIDTH && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            gap: 10,
          }}
        >
          {scheduleToDays.map((el, indexEl) => {
            const isSelected = currentDate === moment(el.weekday).day();
            const isToday = isTodayInTimeZone(el.weekday, timeZone);
            const isWeekend = [6, 0].includes(moment(el.weekday).day());

            return (
              <div
                key={indexEl}
                style={{
                  flex: 1,
                  height: screenWidth < 585 ? "60px" : "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: isSelected ? color_red_default : color_white,
                  color: isSelected ? color_white : color_black,
                  borderRadius: 10,
                  cursor: "pointer",
                  boxSizing: "border-box",
                  boxShadow:
                    !isSelected && isToday
                      ? `inset 0 0 0 2px ${isWeekend ? color_black : color_red_default}`
                      : "none",
                }}
                onClick={() => setCurrentDate(moment(el.weekday).day())}
              >
                <OnestSemiBoldDefault>
                  {moment(el.weekday)
                    .locale(locale === "en" ? "en-gb" : "ru")
                    .format("dd")
                    .toUpperCase()}
                </OnestSemiBoldDefault>
                <OnestNormalDefault>
                  {moment(el.weekday).format("DD")}
                </OnestNormalDefault>
              </div>
            );
          })}
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
          gap: screenWidth < 1440 ? 8 : 10,
          minHeight: calendarMinHeight,
          height: "auto",
        }}
      >
        {scheduleToDays.map((el) => {
          const isToday = isTodayInTimeZone(el.weekday, timeZone);
          const showDay =
            screenWidth >= MOBILE_CALENDAR_WIDTH ||
            currentDate === el.weekday.day();

          if (!showDay) return null;
          return (
            <ScheduleDay
              key={el.weekday.toISOString()}
              {...dayProps(el, isToday)}
            />
          );
        })}
      </div>
    </>
  );
};

export default ScheduleCalendar;
